import React, { useState } from "react";
import { CASES } from "../mock";
import { ArrowUpRight, X, Play } from "lucide-react";

export default function Cases() {
  const [active, setActive] = useState(null);

  return (
    <section id="cas" className="relative py-24 md:py-40 px-6 md:px-10 border-t border-white/5">
      <div className="max-w-[1440px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div>
            <span className="font-mono text-xs uppercase tracking-widest text-[#FF5B24]">
              (04) — Réalisations
            </span>
            <h2 className="font-display font-medium text-4xl md:text-7xl mt-6 leading-[1.02] tracking-[-0.03em]">
              Des marques que <br />
              nous aimons.{" "}
              <span className="font-serif-i text-white/60">Et réciproquement.</span>
            </h2>
          </div>
          <div className="font-mono text-xs uppercase tracking-widest text-white/40">
            04 cas sélectionnés / {new Date().getFullYear()}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {CASES.map((c, i) => (
            <button
              key={c.id}
              onClick={() => setActive(c)}
              className={`case-card group text-left relative overflow-hidden rounded-lg bg-black/30 backdrop-blur-sm border border-white/10 ${
                i % 3 === 0 ? "md:col-span-2" : ""
              }`}
            >
              <div className={`relative overflow-hidden ${i % 3 === 0 ? "aspect-[21/9]" : "aspect-[4/3]"}`}>
                <img
                  src={c.image}
                  alt={c.client}
                  className="case-image w-full h-full object-cover opacity-80 group-hover:opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-500 mix-blend-multiply"
                  style={{ background: c.color }}
                />
                <div className="absolute top-5 left-5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/70">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.color }} />
                  {c.period}
                </div>
                <div className="absolute top-5 right-5 w-10 h-10 rounded-full border border-white/30 flex items-center justify-center group-hover:bg-[#FF5B24] group-hover:border-[#FF5B24] transition-all">
                  {c.video ? <Play className="w-4 h-4 fill-current" /> : <ArrowUpRight className="w-4 h-4" />}
                </div>
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2">
                    {c.category}
                  </div>
                  <div className="font-display text-3xl md:text-5xl font-medium tracking-[-0.02em]">
                    {c.client}
                  </div>
                  <div className="font-serif-i text-white/70 text-lg md:text-xl mt-1">
                    {c.title}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Case detail modal */}
      {active && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-10 overflow-y-auto"
          onClick={() => setActive(null)}
        >
          <div
            className="relative bg-[#1a0d3a]/95 backdrop-blur-xl border border-white/10 rounded-lg max-w-4xl w-full my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActive(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-[#FF5B24] flex items-center justify-center"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="relative aspect-[16/9] overflow-hidden rounded-t-lg bg-black">
              {active.video ? (
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${active.video}?autoplay=1&rel=0&modestbranding=1`}
                  title={`${active.client} — ${active.title}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <>
                  <img src={active.image} alt={active.client} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                      {active.category} — {active.period}
                    </div>
                    <div className="font-display text-4xl md:text-6xl font-medium mt-2">{active.client}</div>
                    <div className="font-serif-i text-xl md:text-2xl text-white/80">{active.title}</div>
                  </div>
                </>
              )}
            </div>
            {active.video && (
              <div className="px-6 md:px-10 pt-6 pb-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                  {active.category} — {active.period}
                </div>
                <div className="font-display text-3xl md:text-5xl font-medium mt-2">{active.client}</div>
                <div className="font-serif-i text-lg md:text-2xl text-white/80">{active.title}</div>
              </div>
            )}
            <div className="p-6 md:p-10 grid md:grid-cols-2 gap-8">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5B24] mb-3">
                  Le challenge
                </div>
                <p className="text-white/80 leading-relaxed">{active.challenge}</p>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5B24] mb-3">
                  La solution
                </div>
                <p className="text-white/80 leading-relaxed">{active.solution}</p>
              </div>
              <div className="md:col-span-2 border-t border-white/10 pt-8">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5B24] mb-6">
                  Résultats
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {active.results.map((r) => (
                    <div key={r.label} className="border-l border-white/10 pl-4">
                      <div className="font-display text-3xl md:text-4xl font-medium" style={{ color: active.color }}>
                        {r.value}
                      </div>
                      <div className="text-xs text-white/60 mt-1">{r.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
